import React from 'react';
import { Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Import bundled data for fallback CSV generation
import chronicMapData from '../data/chronicMapData.json';
import chronicDiseases from '../data/chronicDiseases.json';
import autoimmuneDiseases from '../data/autoimmuneDiseases.json';

// Authoritative source URLs that mirror the backend's CSV metadata.
const CDC_PLACES_SOURCE_URL =
  'https://data.cdc.gov/500-Cities-Places/PLACES-Local-Data-for-Better-Health-County-Data-20/swc5-untb';
const CDC_VACCINE_SOURCE_URL =
  'https://data.cdc.gov/Public-Health-Surveillance/Weekly-Respiratory-Pathogens-and-Vaccination-RPV-M/5c6r-xi2t';
const NIAMS_AUTOIMMUNE_FALLBACK_URL =
  'https://www.niams.nih.gov/health-topics/autoimmune-diseases';

function csvEscape(value) {
  const v = value === null || value === undefined ? '' : String(value);
  return `"${v.replace(/"/g, '""')}"`;
}

function generateCsvFromBundledData(dataset, disease) {
  const rows = [];
  const generatedAt = `${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC`;

  if (dataset === 'chronic') {
    const mapData = chronicMapData[disease] || [];
    const diseaseInfo = chronicDiseases.diseases?.[disease] || {};
    const source = diseaseInfo.source || 'CDC PLACES 2024';
    const sourceUrl = diseaseInfo.source_url || CDC_PLACES_SOURCE_URL;
    const usAvg = diseaseInfo.us_prevalence ?? '';
    const label = diseaseInfo.name || disease;
    const methodology =
      "Source dataset: CDC PLACES county file (data.cdc.gov resource swc5-untb). " +
      "PLACES publishes a small-area model-based crude prevalence for each U.S. county using BRFSS responses. " +
      "PER-COLUMN: 'State' = USPS abbreviation. " +
      "'Rate (%)' = SUM(county_rate × county_pop18plus) / SUM(county_pop18plus) — population-weighted state prevalence. " +
      "'US Average (%)' = the same SUM/SUM formula nationally per disease. " +
      "'Difference' = Rate − US Average (rounded to 1 decimal). " +
      "'Risk Level' = 'high' if Rate > 1.08 × US Average, 'low' if < 0.92 × US Average, else 'moderate'. " +
      "'Data Source' = exact label of the CDC release. 'Source URL' = data.cdc.gov dataset link. " +
      "'Data Status' = 'official' if pulled from live CDC PLACES, 'estimated' if served from bundled fallback (preview mode).";

    rows.push(['# Dataset', `Chronic Disease - ${label}`]);
    rows.push(['# Source', source]);
    rows.push(['# Source URL', sourceUrl]);
    rows.push(['# Methodology', methodology]);
    rows.push(['# Generated', generatedAt]);
    rows.push([]);
    rows.push(['State', 'Rate (%)', 'US Average (%)', 'Risk Level', 'Difference', 'Data Source', 'Source URL', 'Data Status']);
    mapData.forEach((s) => {
      const rate = s.rate ?? '';
      const diff = typeof rate === 'number' && typeof usAvg === 'number' ? Math.round((rate - usAvg) * 10) / 10 : '';
      rows.push([s.state, rate, usAvg, s.risk_level || '', diff, source, sourceUrl, s.data_status || 'official']);
    });
  } else if (dataset === 'autoimmune') {
    const diseaseInfo = autoimmuneDiseases.diseases?.[disease] || {};
    const source = diseaseInfo.source || 'Primary national source — see source_url';
    const sourceUrl = diseaseInfo.source_url || NIAMS_AUTOIMMUNE_FALLBACK_URL;
    const diseaseName = diseaseInfo.name || disease;
    const nationalPrev = diseaseInfo.us_prevalence ?? '';
    const methodology =
      "CRITICAL TRANSPARENCY NOTICE: There is NO U.S. federal surveillance system that publishes state-level prevalence " +
      `for autoimmune diseases including ${diseaseName}. CDC PLACES does not cover autoimmune conditions. ` +
      `The 'Prevalence Rate (%)' column shows the SAME national prevalence (${nationalPrev}%) for every state, sourced ` +
      "from the disease's primary national authority (see 'Source URL'). DO NOT infer state-level burden from this CSV — " +
      "real geographic variation exists but is not measured by any federal program. " +
      "PER-COLUMN: 'State' = USPS abbreviation; 'Disease' = internal key; 'Prevalence Rate (%)' = national prevalence repeated; " +
      "'Disease Name' = human-readable name; 'Data Source' = name of national source; 'Source URL' = link to that source.";

    rows.push(['# Dataset', `Autoimmune Disease - ${diseaseName}`]);
    rows.push(['# Source', source]);
    rows.push(['# Source URL', sourceUrl]);
    rows.push(['# Methodology', methodology]);
    rows.push(['# Data Coverage', 'NATIONAL only — no state-level surveillance exists']);
    rows.push(['# Generated', generatedAt]);
    rows.push([]);
    rows.push(['State', 'Disease', 'Prevalence Rate (%)', 'Disease Name', 'Data Source', 'Source URL', 'Data Status']);
    // Emit a national-prevalence row for every state with explicit national_estimate_only flag.
    const STATES = ['AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'];
    STATES.forEach((st) => {
      rows.push([st, disease, nationalPrev, diseaseName, source, sourceUrl, 'national_estimate_only']);
    });
  } else if (dataset === 'vaccine') {
    const source = 'CDC Vaccination Coverage — bundled state estimates';
    const sourceUrl = CDC_VACCINE_SOURCE_URL;
    const methodology =
      "TRANSPARENCY NOTICE: Per-state vaccine coverage values are BUNDLED ESTIMATES approximating the most-recent CDC " +
      "published state coverage (BRFSS Adult Vaccination, SchoolVaxView Kindergarten, TeenVaxView NIS-Teen). They are NOT " +
      "pulled live per state. For authoritative current per-state coverage, use the CDC dashboards: " +
      "Adult — https://www.cdc.gov/vaccines/imz-managers/coverage/adult-vaxview/index.html ; " +
      "Kindergarten MMR — https://www.cdc.gov/vaccines/imz-managers/coverage/schoolvaxview/ ; " +
      "HPV ages 13–17 — https://www.cdc.gov/vaccines/imz-managers/coverage/teenvaxview/ . " +
      "PER-COLUMN: 'State' = USPS abbreviation; 'COVID/Flu Coverage' approx. BRFSS Adult; 'Childhood MMR' approx. SchoolVaxView; " +
      "'HPV' approx. TeenVaxView; 'Data Status' = 'bundled_estimate'.";
    rows.push(['# Dataset', 'Vaccination Coverage by State']);
    rows.push(['# Source', source]);
    rows.push(['# Source URL', sourceUrl]);
    rows.push(['# Methodology', methodology]);
    rows.push(['# Data Coverage', 'Bundled estimates — see CDC dashboards above for authoritative state coverage']);
    rows.push(['# Generated', generatedAt]);
    rows.push([]);
    rows.push(['Info', 'Note']);
    rows.push(['Source URL', sourceUrl]);
    rows.push(['Note', 'For per-state coverage, use the live API export endpoint or the CDC dashboards in the Methodology line.']);
  }

  return rows.map((r) => r.map(csvEscape).join(',')).join('\n');
}

export const CsvDownloadButton = ({ dataset = 'chronic', disease = 'diabetes', label }) => {
  const handleDownload = async () => {
    // Try API first
    if (API) {
      try {
        const res = await fetch(`${API}/api/export/csv?dataset=${dataset}&disease=${disease}`, { signal: AbortSignal.timeout(5000) });
        if (res.ok) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${dataset}_${disease}_data.csv`;
          a.click();
          URL.revokeObjectURL(url);
          return;
        }
      } catch {
        // Fall through to bundled data
      }
    }

    // Fallback: generate CSV from bundled data (still includes source URL)
    const csv = generateCsvFromBundledData(dataset, disease);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${dataset}_${disease}_data.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <button
      onClick={handleDownload}
      data-testid={`csv-download-${dataset}-${disease}`}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-all"
    >
      <Download className="w-3.5 h-3.5" />
      {label || 'Download CSV'}
    </button>
  );
};
